import { z } from 'zod';
import { MoneySchema } from './common.js';
// Decimal columns serialize as canonical strings; accept either on the wire.
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
const DecimalLikeNonNeg = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const FixedAssetSourceSchema = z.enum(['MANUAL', 'FROM_CLEARING', 'FROM_CIP']);
export const FixedAssetStatusSchema = z.enum(['ACTIVE', 'DISPOSED', 'FULLY_DEPRECIATED']);
export const DepreciationMethodSchema = z.enum(['STRAIGHT_LINE']);
export const DepreciationEntryStatusSchema = z.enum(['PENDING', 'POSTED']);
// ============================================================
// FixedAsset — persisted shape
// ============================================================
export const FixedAssetSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    code: z.string().min(1),
    name: z.string().min(1),
    categoryCode: z.string(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    acquisitionDate: z.coerce.date(),
    costAmount: DecimalLikeNonNeg,
    salvageValue: DecimalLikeNonNeg,
    usefulLifeMonths: z.number().int().positive(),
    depreciationMethod: DepreciationMethodSchema,
    accumulatedDepreciation: DecimalLikeNonNeg,
    nbv: DecimalLike,
    status: FixedAssetStatusSchema,
    disposedDate: z.coerce.date().nullable().optional(),
    disposedAmount: DecimalLike.nullable().optional(),
    journalEntryId: z.uuid().nullable().optional(),
    disposedJournalEntryId: z.uuid().nullable().optional(),
    sourceType: FixedAssetSourceSchema.optional(),
    sourceJournalLineId: z.uuid().nullable().optional(),
    fundingAccountCode: z.string().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List row — adds derived display names + categoryName.
export const FixedAssetListRowSchema = FixedAssetSchema.extend({
    branchName: z.string().nullable().optional(),
    categoryName: z.string().nullable().optional(),
});
// ============================================================
// Create — POST body (acquisition)
// ============================================================
export const CreateFixedAssetSchema = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1),
    categoryCode: z.string().min(1),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    acquisitionDate: z.iso.date(),
    costAmount: z.coerce.number().positive(),
    salvageValue: z.coerce.number().nonnegative().default(0),
    usefulLifeMonths: z.coerce.number().int().positive(),
    // V1 fixed to STRAIGHT_LINE; explicit enum so the wire shape is forward-compatible.
    depreciationMethod: DepreciationMethodSchema.default('STRAIGHT_LINE'),
    // Funding-source COA code on the credit side of the acquisition JV.
    // Optional — when omitted the service resolves the company's AP_TRADE
    // account via AccountResolver. Pass an explicit code (e.g. a cash/bank
    // account) for cash purchases.
    fundingAccountCode: z.string().min(1).optional(),
    // Optional per-asset expense code; if omitted, dep-run resolves the
    // company's FA_DEP_EXPENSE account (or the per-category override).
    expenseAccountCode: z.string().min(1).optional(),
});
// ============================================================
// Dispose — POST :id/dispose
// ============================================================
export const DisposeFixedAssetSchema = z.object({
    date: z.iso.date(),
    amount: z.coerce.number().nonnegative(),
});
// ============================================================
// CapitalizeReceipt — POST receipts/:journalLineId/receive
// Receive a clearing line into the register. Asset-master fields the inbox
// can't infer (category / useful life / dep start) are supplied here.
// ============================================================
export const CapitalizeReceiptSchema = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1),
    categoryCode: z.string().min(1),
    usefulLifeMonths: z.coerce.number().int().positive(),
    salvageValue: z.coerce.number().nonnegative().default(0),
    acquisitionDate: z.iso.date().optional(),
    depreciationStartDate: z.iso.date().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
});
// ============================================================
// DepreciationEntry — schedule row + run-batch trigger
// ============================================================
export const DepreciationEntrySchema = z.object({
    id: z.uuid(),
    assetId: z.uuid(),
    period: z.string().regex(/^\d{6}$/),
    amount: DecimalLikeNonNeg,
    accumulatedAfter: DecimalLikeNonNeg,
    status: DepreciationEntryStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const RunDepreciationSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
});
// Response from the run-depreciation endpoint.
export const RunDepreciationResponseSchema = z.object({
    period: z.string(),
    entryCount: z.number().int().nonnegative(),
    totalAmount: DecimalLikeNonNeg,
    journalEntryId: z.uuid().nullable(),
    idempotentReplay: z.boolean(),
});
// ============================================================
// PendingReceiptRow — open FA_CLEARING debit lines awaiting register entry
// ============================================================
export const PendingReceiptRowSchema = z.object({
    journalLineId: z.uuid(),
    voucherNo: z.string(),
    voucherDate: z.coerce.date(),
    amount: z.string(),
    description: z.string().nullable(),
    branchId: z.uuid().nullable(),
    departmentId: z.uuid().nullable(),
    source: z.enum(['AP_BILL', 'MANUAL_JV']),
    sourceRef: z.string().nullable(),
});
// ============================================================
// FaReconciliation — register ↔ GL control account comparison
// ============================================================
const ReconRowSchema = z.object({
    label: z.string(),
    register: z.string(),
    gl: z.string(),
    diff: z.string(),
});
export const FaReconciliationSchema = z.object({
    asOfPeriod: z.string().nullable(),
    rows: z.array(ReconRowSchema),
    pendingCount: z.number().int(),
});
//# sourceMappingURL=fixed-asset.js.map